import { DateInput, DatePicker as DatePickerConstellation } from '@zillow/constellation-10';
import { forwardRef, useMemo, useState } from 'react';
import { displayDateFormat } from '../../constants/applicationsConstants';
import { getFormattedDateValue, parseDisplayDateToServerDate } from '../../utils/formatters';

type FieldProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name?: string;
};

export type DatePickerProps = {
  minDate?: Date;
  maxDate?: Date;
} & FieldProps;

/**
 * A DatePicker component that integrates with react-hook-form.
 *
 * This component handles the conversion between server date format (yyyy-MM-dd)
 * and display date format for the user.
 *
 * Features:
 * - Parses typed dates on blur (e.g., "05/23/1988" -> "May 23, 1988")
 * - Supports calendar selection
 * - Converts between display format and server format automatically
 *
 * Note: Ensure minDate and maxDate are stable across renders (use useMemo or
 * stable date functions like today()) to avoid layout issues.
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({ value, onChange, onBlur, minDate: minDateUnstable, maxDate: maxDateUnstable, ...props }, ref) => {
    const minDateValue = minDateUnstable?.valueOf();
    const maxDateValue = maxDateUnstable?.valueOf();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed by timestamp value, not object reference; preserving existing behavior
    const minDate = useMemo(() => minDateUnstable, [minDateValue]);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed by timestamp value, not object reference; preserving existing behavior
    const maxDate = useMemo(() => maxDateUnstable, [maxDateValue]);

    const [inputValue, setInputValue] = useState<string | null>(null);

    const displayValue = inputValue ?? getFormattedDateValue(value);

    const handleValueChange = (newValue: Date | string, { reason }: { reason: string }) => {
      const stringValue = typeof newValue === 'string' ? newValue : newValue.toISOString();
      const serverDate = parseDisplayDateToServerDate(stringValue);
      setInputValue(stringValue);
      onChange(serverDate);
      // oxlint-disable-next-line constellation-content/voice-and-tone
      if (reason === 'dropdown click') {
        onBlur();
      }
    };

    const handleBlur = () => {
      if (inputValue !== null) {
        const serverDate = parseDisplayDateToServerDate(inputValue, true);
        onChange(serverDate);
        setInputValue(null);
      }

      onBlur();
    };

    return (
      <DatePickerConstellation
        value={displayValue}
        onValueChange={handleValueChange}
        dateFormat={displayDateFormat}
        minDate={minDate}
        maxDate={maxDate}
        closeModalOnDateSelect
        input={<DateInput ref={ref} onBlur={handleBlur} {...props} />}
      />
    );
  },
);

DatePicker.displayName = 'DatePicker';
